import { ChakraGrid, ChakraGridItem, ChakraVStack } from '@equip.health/ui';

import ExpandableSection from '~/components/common/inquiry/drawer/ExpandableSection';
import { EditMode } from '~/components/common/inquiry/drawer/types';
import { UseEditInquiryState } from '~/components/common/inquiry/drawer/useEditInquiry';

import ContactInfo from './ContactInfo';
import CoverageInfo from './CoverageInfo';
import InquiryInfo from './InquiryInfo';
import InquiryNote from './InquiryNote';
import PatientInfo from './PatientInfo';
import ReferralInfo from './ReferralInfo';

export interface EditInquiryProps {
  editInquiryState: UseEditInquiryState;
  editMode: EditMode;
  showRequiredFieldErrors: boolean;
}

const EditInquiry = ({
  editInquiryState,
  editMode,
  showRequiredFieldErrors,
}: EditInquiryProps) => {
  const {
    info,
    updateInfo,
    patient,
    updatePatient,
    contacts,
    updateContact,
    addContact,
    coverage,
    updateCoverage,
    referral,
    updateReferral,
    note,
    updateNote,
  } = editInquiryState;

  const TOP_LEVEL_ID = 'edit-inquiry';

  /* 
  const shouldShowReferrerTypeField = info.inquirerRole === 'Referrer';
  Reverting this change for now since we need more clarity around
  referrer type values and the impact in other fields.
  */

  const shouldShowReferrerTypeField = true;

  return (
    <ChakraGrid
      columnGap={{ base: 0, lg: '40px' }}
      templateColumns={{ base: 'repeat(1, 1fr)', lg: 'repeat(3, 1fr)' }}
    >
      <ChakraGridItem
        colSpan={{ base: 1, lg: 2 }}
        order={{ base: 1, lg: 0 }}
        paddingLeft="1px"
        paddingRight="8px"
      >
        <ChakraVStack alignItems="flex-start" gap="36px" paddingY="24px">
          <InquiryInfo
            editMode={editMode}
            inquiryInfoValue={info}
            showRequiredFieldErrors={showRequiredFieldErrors}
            updateInquiryInfo={updateInfo}
          />
          <PatientInfo
            patientAge={info?.patientAge}
            patientInfoValue={patient}
            showRequiredFieldErrors={showRequiredFieldErrors}
            updateInfo={updateInfo}
            updatePatientInfo={updatePatient}
          />
          <ExpandableSection
            addButtonText="Add another contact"
            entityType="Contact"
            headerText="Contact Information"
            id={TOP_LEVEL_ID}
            onAdd={addContact}
          >
            {contacts.map((x, i) => (
              <ContactInfo
                contactInfoValue={x}
                id={`${TOP_LEVEL_ID}__contact-info-${i}`}
                key={i}
                showRequiredFieldErrors={showRequiredFieldErrors}
                updateContactInfo={(x) => updateContact(i, x)}
              />
            ))}
          </ExpandableSection>
          <CoverageInfo
            coverageInfoValue={coverage}
            showRequiredFieldErrors={showRequiredFieldErrors}
            updateCoverageInfo={updateCoverage}
          />
          <ReferralInfo
            referralInfoValue={referral}
            shouldShowReferrerTypeField={shouldShowReferrerTypeField}
            showRequiredFieldErrors={showRequiredFieldErrors}
            updateReferralInfo={updateReferral}
          />
        </ChakraVStack>
      </ChakraGridItem>
      <ChakraGridItem colSpan={1} marginTop="24px" rowSpan={2}>
        <InquiryNote noteValue={note} setNoteValue={updateNote} />
      </ChakraGridItem>
    </ChakraGrid>
  );
};

export default EditInquiry;
